import { apolloQueryWrapper } from '@/shared/api';
import HOLTER_EVENTS_QUERY from '@/entities/event/api/queries/holterEvents';
import type { HolterEventsQuery, HolterEventsQueryVariables } from '@/shared/api/generated/graphql';

export type HolterEventStrip = {
  images: string[];
  id: string;
  draw: any;
  isSelected: boolean;
  start: string;
  stop: string;
  comment: string;
  noiseChannels: number[];
  channel: number;
  userUpdated: boolean;
};

export type HolterEventListItem = {
  id: string;
  start: string;
  stop: string;
  type: string;
  minHr: number;
  maxHr: number;
  avgHr: number;
  isReviewed: boolean;
  channel: number;
  comment: string;
  isCaptured: boolean;
  title: string;
  countBeats: number;
  duration: string;
  strips: HolterEventStrip[];
};

export type HolterEvents = {
  isSuccess: boolean;
  message: string;
  events: HolterEventListItem[];
};

// TODO(ts): typed as the HolterEventsFilter GraphQL input once its server-side shape is documented
/**
 * Loads a page of Holter ECG events for the report tabs. Runs the holterEvents query with the filter and page limit and returns the whole payload, throwing the server message when isSuccess is false.
 *
 * @param filter - GraphQL filter narrowing the events to a study, type and time range.
 * @param limit - Page size, defaulting to ten.
 * @param isUsingCache - When true, lets the wrapper serve a cached result.
 * @param key - Cancellation key identifying this request.
 * @param needCancel - Whether an in-flight request under the same key is cancelled first.
 * @returns A promise resolving to the holterEvents payload.
 */
const fetchHolterEvents = async (
  filter: any,
  limit = 10,
  isUsingCache?: boolean,
  key?: string | null,
  needCancel = true,
) => {
  const result = await apolloQueryWrapper<HolterEventsQuery, HolterEventsQueryVariables>({
    options: {
      query: HOLTER_EVENTS_QUERY,
      variables: {
        filter,
        limit,
      },
    },
    isUsingCache,
    key,
    needCancel,
  });
  const { data } = result;
  const { holterEvents } = data;
  if (holterEvents?.isSuccess) {
    return holterEvents;
  }
  throw new Error(holterEvents.message);
};

export default fetchHolterEvents;
